import { Services } from '../../services';
import { LEGEND_ITEMS, type LegendItemId } from './map.legend';
import type { MaybeSpanType } from './map.line/map.line';

export type LabelZoomType = MaybeSpanType | 'pole' | 'photoLocation' | 'midspanPhoto';

/**
 * Minimum (floored) map zoom at which each label type appears. Coarsest types (pole titles)
 * reveal first so a zoomed-out whole-job view stays legible. Values match PoleOS so the two
 * apps declutter consistently; a type absent here is never gated.
 */
export const LABEL_ZOOM_MIN: { [key in LabelZoomType]?: number } = {
	pole: 16,
	span: 18,
	dropspan: 18,
	unknown: 18,
	anchor: 18,
	'vector-anchor': 18,
	// The photo-location marker (device icon + FOV cone + distance label); gated like a label so it
	// stops cluttering a zoomed-out view even while its capture is selected.
	photoLocation: 18,
	// MidSpan capture dots sit on the span line, so they reveal with spans rather than earlier.
	midspanPhoto: 18,
};

/**
 * Whether a label of `type` should show at `zoom`. Returns true when zoom is unknown (no camera
 * yet) or the type has no tier, so the gating fallback is "show", never "hide".
 */
export function isLabelTypeVisibleAtZoom(type: LabelZoomType, zoom: number | undefined): boolean {
	if (zoom == null) {
		return true;
	}
	const tier = LABEL_ZOOM_MIN[type];
	return tier == null || Math.floor(zoom) >= tier;
}

const DISPLAY_SETTINGS_KEY = 'topologicalEditorDisplay';

export interface TopologicalEditorDisplaySettings {
	hiddenLegendItems: LegendItemId[];
	/** Hidden line-type visibility keys (dynamic, per-form). Kept separate from the static `hiddenLegendItems`. */
	hiddenLineTypes: string[];
	/** Hidden asset-type visibility keys (dynamic, per-form). Kept separate from the static `hiddenLegendItems`. */
	hiddenAssetTypes: string[];
	/** Whether span/pole labels are shown in Map Mode. Persisted so the choice survives re-entry. */
	viewLabels: boolean;
}

export function readDisplaySettings(): TopologicalEditorDisplaySettings {
	const stored = Services.LocalStorage.getItem<Partial<TopologicalEditorDisplaySettings> | null>(DISPLAY_SETTINGS_KEY);
	// Drop ids that no longer exist (stale or hand-edited storage)
	const knownIds = new Set<LegendItemId>(
		LEGEND_ITEMS.map((item) => item.id).filter((id): id is LegendItemId => id != null)
	);
	return {
		hiddenLegendItems: Array.isArray(stored?.hiddenLegendItems)
			? stored.hiddenLegendItems.filter((id) => knownIds.has(id))
			: [],
		// Line-type/asset-type keys are dynamic (per-form), so they can't be validated against a static list; kept as-is.
		hiddenLineTypes: Array.isArray(stored?.hiddenLineTypes)
			? stored.hiddenLineTypes.filter((k) => typeof k === 'string')
			: [],
		hiddenAssetTypes: Array.isArray(stored?.hiddenAssetTypes)
			? stored.hiddenAssetTypes.filter((k) => typeof k === 'string')
			: [],
		viewLabels: typeof stored?.viewLabels === 'boolean' ? stored.viewLabels : false,
	};
}

export function writeDisplaySettings(settings: TopologicalEditorDisplaySettings): void {
	Services.LocalStorage.setItem(DISPLAY_SETTINGS_KEY, settings);
}
